import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { getConfig } from '../runtimeConfig';
import { cachedFetch } from '../cacheManager';

// Types
interface FieldDefinition {
  key: string;
  dbColumn: string;
  label: string;
  category: 'identity' | 'location' | 'logistics' | 'quantity' | 'tracking' | 'dates' | 'status';
  description: string;
}

interface SelectedField {
  key: string;
  label: string;
  customHeader?: string;
  columnWidth?: 'narrow' | 'medium' | 'wide' | number;
}

interface Template {
  id?: string;
  name: string;
  customerCode?: string;
  fields: SelectedField[];
  isDefault?: boolean;
  createdDate?: string;
  scheduleId?: string;
}

interface ExcelTemplateBuilderProps {
  customerList?: { code: string; name: string }[];
  onSave?: (template: Template) => void;
  onClose?: () => void;
  initialTemplate?: Template;
  scheduleId?: string;
  mode?: 'standalone' | 'embedded';
}

// Available fields from DB2
const AVAILABLE_FIELDS: FieldDefinition[] = [
  { key: 'CustomerName', dbColumn: 'BMFIRM', label: 'Customer Name', category: 'identity', description: 'Customer/Company code' },
  { key: 'ItemNo', dbColumn: 'BMIDEN', label: 'Item Number', category: 'identity', description: 'Item identification number' },
  { key: 'Description', dbColumn: 'TZBEZ1', label: 'Description', category: 'identity', description: 'Item description' },
  { key: 'AdditionalDescription', dbColumn: 'TZBEZ2', label: 'Additional Description', category: 'identity', description: 'Secondary item description' },
  { key: 'CHM', dbColumn: 'BMORT', label: 'CHM', category: 'location', description: 'CHM code' },
  { key: 'StorageLocation', dbColumn: 'BMBER', label: 'Storage Location', category: 'location', description: 'Storage area location' },
  { key: 'RackNo', dbColumn: 'BMREG||BMHOR||BMVER', label: 'Rack Number', category: 'location', description: 'Rack position (Row-Horizontal-Vertical)' },
  { key: 'TrailerNumber', dbColumn: 'BMRA15', label: 'Trailer Number', category: 'logistics', description: 'Trailer/container number' },
  { key: 'LUNumber', dbColumn: 'BMLENR', label: 'LU Number', category: 'logistics', description: 'Loading unit number' },
  { key: 'GRNumber', dbColumn: 'BMWENR', label: 'GR Number', category: 'logistics', description: 'Goods receipt number' },
  { key: 'PONumber', dbColumn: 'BMBST1', label: 'PO Number', category: 'logistics', description: 'Purchase order number' },
  { key: 'TotalQuantity', dbColumn: 'BMVMEN', label: 'Total Quantity', category: 'quantity', description: 'Total available quantity' },
  { key: 'QU', dbColumn: 'BMME', label: 'Quantity Unit', category: 'quantity', description: 'Unit of measurement' },
  { key: 'KG', dbColumn: 'CALCULATED', label: 'Weight (KG)', category: 'quantity', description: 'Calculated weight in kilograms' },
  { key: 'BatchLot', dbColumn: 'BMPRNR', label: 'Batch/Lot', category: 'tracking', description: 'Batch or lot number' },
  { key: 'GRReceivingDate', dbColumn: 'BMDTEE', label: 'GR Receiving Date', category: 'dates', description: 'Goods receipt date' },
  { key: 'ProductionDate', dbColumn: 'BMRN9', label: 'Production Date', category: 'dates', description: 'Manufacturing/production date' },
  { key: 'ExpiryDate', dbColumn: 'BMDTDC', label: 'Expiry Date', category: 'dates', description: 'Product expiry date' },
  { key: 'DaysBeforeExpiry', dbColumn: 'CALCULATED', label: 'Days Before Expiry', category: 'dates', description: 'Number of days until expiry' },
  { key: 'Remarks', dbColumn: 'BMQCHR', label: 'Remarks', category: 'status', description: 'Quality status (Good Stock, Damaged, Expired, Other)' }
];

const CATEGORY_CONFIG: Record<string, { color: string; icon: string; label: string }> = {
  identity: { color: '#3b82f6', icon: '🏷️', label: 'Identity' },
  location: { color: '#8b5cf6', icon: '📍', label: 'Location' },
  logistics: { color: '#06b6d4', icon: '🚚', label: 'Logistics' },
  quantity: { color: '#f59e0b', icon: '📊', label: 'Quantity' },
  tracking: { color: '#10b981', icon: '🔖', label: 'Tracking' },
  dates: { color: '#ec4899', icon: '📅', label: 'Dates' },
  status: { color: '#ef4444', icon: '📋', label: 'Status' }
};

const SAMPLE_DATA: Record<string, string[]> = {
  CustomerName: ['AFA', 'MAR', 'NES'],
  ItemNo: ['10245', '30891', '55102'],
  Description: ['White Cement 50KG', 'Steel Rebar 12mm', 'PVC Pipe 4inch'],
  AdditionalDescription: ['Premium Grade', 'Grade 60', 'Schedule 40'],
  CHM: ['DSI', 'JDA', 'RUH'],
  StorageLocation: ['01', '03', '02'],
  RackNo: ['A-01-03', 'B-12-01', 'C-05-02'],
  TrailerNumber: ['TR-4521', 'TR-7823', 'TR-1290'],
  LUNumber: ['LU00145', 'LU00891', 'LU00334'],
  GRNumber: ['GR20240015', 'GR20240089', 'GR20240134'],
  PONumber: ['PO-AFA-001', 'PO-MAR-045', 'PO-NES-023'],
  TotalQuantity: ['1,500', '3,200', '850'],
  QU: ['KG', 'PC', 'MTR'],
  KG: ['75,000', '12,800', '2,125'],
  BatchLot: ['BT-2024-001', 'BT-2024-089', 'BT-2024-045'],
  GRReceivingDate: ['15-Jan-2024', '22-Feb-2024', '03-Mar-2024'],
  ProductionDate: ['01-Dec-2023', '15-Jan-2024', '20-Feb-2024'],
  ExpiryDate: ['01-Dec-2025', '15-Jan-2026', '20-Feb-2026'],
  DaysBeforeExpiry: ['620', '665', '701'],
  Remarks: ['Good_Stock', 'Good_Stock', 'Damaged']
};

const QUICK_PRESETS = [
  { name: 'Basic', fields: ['CustomerName', 'ItemNo', 'Description', 'TotalQuantity', 'Remarks'] },
  { name: 'Standard', fields: ['CustomerName', 'ItemNo', 'Description', 'StorageLocation', 'TotalQuantity', 'QU', 'GRReceivingDate', 'ExpiryDate', 'DaysBeforeExpiry', 'Remarks'] },
  { name: 'Full Report', fields: AVAILABLE_FIELDS.map(f => f.key) },
  { name: 'Expiry Focus', fields: ['CustomerName', 'ItemNo', 'Description', 'ExpiryDate', 'DaysBeforeExpiry', 'ProductionDate', 'BatchLot', 'Remarks'] },
  { name: 'Logistics Focus', fields: ['CustomerName', 'ItemNo', 'TrailerNumber', 'LUNumber', 'GRNumber', 'PONumber', 'RackNo', 'StorageLocation'] }
];

// Main Component
const ExcelTemplateBuilder: React.FC<ExcelTemplateBuilderProps> = ({
  customerList = [],
  onSave,
  onClose,
  initialTemplate,
  scheduleId,
  mode = 'standalone'
}) => {
  const API = getConfig().REACT_APP_API;

  // State
  const [templateName, setTemplateName] = useState(initialTemplate?.name || 'My Excel Template');
  const [selectedCustomer, setSelectedCustomer] = useState(initialTemplate?.customerCode || '');
  const [isDefault, setIsDefault] = useState(initialTemplate?.isDefault || false);
  const [selectedFields, setSelectedFields] = useState<SelectedField[]>(initialTemplate?.fields || []);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['identity', 'location', 'logistics', 'quantity', 'tracking', 'dates', 'status']));
  const [savedTemplates, setSavedTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Load saved templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const response = await cachedFetch(`${API}/excel-templates`);
        setSavedTemplates(response);
      } catch (error) {
        console.error('Failed to load templates from API:', error);
        // Fallback to localStorage
        try {
          const savedData = localStorage.getItem('excel-templates');
          if (savedData) {
            const templates = JSON.parse(savedData);
            setSavedTemplates(templates);
            console.log('Loaded templates from localStorage (API unavailable)');
          }
        } catch (localError) {
          console.error('Failed to load templates from localStorage:', localError);
        }
      }
    };
    loadTemplates();
  }, [API]);

  // Get selected field keys for quick lookup
  const selectedFieldKeys = useMemo(() => new Set(selectedFields.map(f => f.key)), [selectedFields]);

  // Filter available fields by search
  const filteredFields = useMemo(() => {
    if (!searchQuery.trim()) return AVAILABLE_FIELDS;
    const query = searchQuery.toLowerCase();
    return AVAILABLE_FIELDS.filter(field =>
      field.label.toLowerCase().includes(query) ||
      field.description.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Group filtered fields by category
  const groupedFields = useMemo(() => {
    const grouped: Record<string, FieldDefinition[]> = {};
    AVAILABLE_FIELDS.forEach(field => {
      if (!grouped[field.category]) grouped[field.category] = [];
      grouped[field.category].push(field);
    });
    return grouped;
  }, []);

  // Count selected per category
  const categorySelectCount = useMemo(() => {
    const counts: Record<string, number> = {};
    selectedFields.forEach(field => {
      const fieldDef = AVAILABLE_FIELDS.find(f => f.key === field.key);
      if (fieldDef) {
        counts[fieldDef.category] = (counts[fieldDef.category] || 0) + 1;
      }
    });
    return counts;
  }, [selectedFields]);

  // Add field to selected
  const addField = useCallback((fieldKey: string) => {
    if (selectedFieldKeys.has(fieldKey)) return;
    const fieldDef = AVAILABLE_FIELDS.find(f => f.key === fieldKey);
    if (fieldDef) {
      setSelectedFields(prev => [...prev, {
        key: fieldKey,
        label: fieldDef.label,
        customHeader: fieldDef.label,
        columnWidth: 'medium'
      }]);
    }
  }, [selectedFieldKeys]);

  // Remove field from selected
  const removeField = useCallback((fieldKey: string) => {
    setSelectedFields(prev => prev.filter(f => f.key !== fieldKey));
  }, []);

  // Move field up/down
  const moveField = useCallback((index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= selectedFields.length) return;
    setSelectedFields(prev => {
      const newFields = [...prev];
      [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
      return newFields;
    });
  }, [selectedFields.length]);

  // Update field custom header
  const updateFieldHeader = useCallback((index: number, newHeader: string) => {
    setSelectedFields(prev => {
      const newFields = [...prev];
      newFields[index].customHeader = newHeader;
      return newFields;
    });
  }, []);

  // Update field column width
  const updateFieldWidth = useCallback((index: number, width: string) => {
    setSelectedFields(prev => {
      const newFields = [...prev];
      newFields[index].columnWidth = isNaN(parseInt(width)) ? (width as 'narrow' | 'medium' | 'wide') : parseInt(width);
      return newFields;
    });
  }, []);

  // Toggle category selection
  const toggleCategory = useCallback((category: string) => {
    const fieldsInCategory = AVAILABLE_FIELDS.filter(f => f.category === category);
    const allSelected = fieldsInCategory.every(f => selectedFieldKeys.has(f.key));

    if (allSelected) {
      // Deselect all in category
      setSelectedFields(prev => prev.filter(f => {
        const fieldDef = AVAILABLE_FIELDS.find(fd => fd.key === f.key);
        return fieldDef?.category !== category;
      }));
    } else {
      // Select all in category
      const fieldsToAdd = fieldsInCategory.filter(f => !selectedFieldKeys.has(f.key));
      setSelectedFields(prev => [...prev, ...fieldsToAdd.map(f => ({
        key: f.key,
        label: f.label,
        customHeader: f.label,
        columnWidth: 'medium' as const
      }))]);
    }
  }, [selectedFieldKeys]);

  // Apply quick preset
  const applyPreset = useCallback((presetFields: string[]) => {
    const newFields = presetFields.map(key => {
      const fieldDef = AVAILABLE_FIELDS.find(f => f.key === key);
      return {
        key,
        label: fieldDef?.label || key,
        customHeader: fieldDef?.label || key,
        columnWidth: 'medium' as const
      };
    });
    setSelectedFields(newFields);
  }, []);

  // Save template
  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      setMessage('Please enter a template name');
      return;
    }
    if (selectedFields.length === 0) {
      setMessage('Please select at least one field');
      return;
    }

    setLoading(true);
    const template: Template = {
      id: initialTemplate?.id,
      name: templateName,
      customerCode: selectedCustomer,
      fields: selectedFields,
      isDefault,
      scheduleId,
      createdDate: new Date().toISOString()
    };

    let savedToServer = false;
    try {
      if (initialTemplate?.id) {
        await axios.put(`${API}/excel-templates/${initialTemplate.id}`, template);
        setMessage('✓ Template updated (server)');
      } else {
        const response = await axios.post(`${API}/excel-templates`, template);
        template.id = response.data.id;
        setMessage('✓ Template saved (server)');
      }
      savedToServer = true;
      onSave?.(template);
    } catch (error) {
      console.error('Failed to save to server:', error);
      // Fallback to localStorage
      try {
        if (!template.id) {
          template.id = `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        }
        const savedData = localStorage.getItem('excel-templates') || '[]';
        const templates = JSON.parse(savedData);
        const existingIndex = templates.findIndex((t: Template) => t.id === template.id);

        if (existingIndex >= 0) {
          templates[existingIndex] = template;
        } else {
          templates.push(template);
        }

        localStorage.setItem('excel-templates', JSON.stringify(templates));
        setSavedTemplates(templates);
        setMessage('✓ Template saved (local storage)');
        onSave?.(template);
      } catch (localError) {
        console.error('Failed to save to localStorage:', localError);
        setMessage('Failed to save template');
      }
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  // Delete template
  const handleDeleteTemplate = async (templateId?: string) => {
    if (!templateId) return;
    let deletedFromServer = false;

    try {
      await axios.delete(`${API}/excel-templates/${templateId}`);
      deletedFromServer = true;
      setMessage('Template deleted from server');
    } catch (error) {
      console.error('Failed to delete from server:', error);
      // Fallback to localStorage
      try {
        const savedData = localStorage.getItem('excel-templates') || '[]';
        const templates = JSON.parse(savedData);
        const filtered = templates.filter((t: Template) => t.id !== templateId);
        localStorage.setItem('excel-templates', JSON.stringify(filtered));
        setMessage('Template deleted from local storage');
      } catch (localError) {
        console.error('Failed to delete from localStorage:', localError);
        setMessage('Failed to delete template');
      }
    } finally {
      // Update UI regardless of success
      setSavedTemplates(prev => prev.filter(t => t.id !== templateId));
      setTimeout(() => setMessage(''), 2000);
    }
  };

  // Load saved template
  const loadTemplate = (template: Template) => {
    setTemplateName(template.name);
    setSelectedCustomer(template.customerCode || '');
    setSelectedFields(template.fields);
    setIsDefault(template.isDefault || false);
  };

  // Toggle category expansion
  const toggleCategoryExpansion = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  // Get column width in pixels
  const getColumnWidth = (width?: string | number): number => {
    if (typeof width === 'number') return width;
    switch (width) {
      case 'narrow': return 10;
      case 'wide': return 25;
      default: return 15;
    }
  };

  // Container styles
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: mode === 'standalone' ? '100vh' : '100%',
    backgroundColor: 'rgba(15, 23, 42, 1)',
    color: '#e2e8f0',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    overflow: 'hidden'
  };

  const topBarStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px 24px',
    backgroundColor: 'rgba(30, 41, 59, 0.8)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    zIndex: 10
  };

  const inputStyle: React.CSSProperties = {
    flex: 1,
    padding: '10px 14px',
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '6px',
    color: '#e2e8f0',
    fontSize: '14px',
    transition: 'all 0.2s ease',
    outline: 'none'
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    flex: 0.5,
    cursor: 'pointer'
  };

  const buttonStyle: React.CSSProperties = {
    padding: '10px 18px',
    backgroundColor: '#4facfe',
    border: 'none',
    borderRadius: '6px',
    color: '#fff',
    fontWeight: '600',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  };

  const mainContentStyle: React.CSSProperties = {
    display: 'flex',
    flex: 1,
    gap: '16px',
    padding: '16px',
    overflow: 'hidden'
  };

  const panelStyle: React.CSSProperties = {
    backgroundColor: 'rgba(30, 41, 59, 0.4)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: '16px'
  };

  const leftPanelStyle: React.CSSProperties = {
    ...panelStyle,
    flex: '0 0 35%'
  };

  const centerPanelStyle: React.CSSProperties = {
    ...panelStyle,
    flex: '0 0 35%'
  };

  const rightPanelStyle: React.CSSProperties = {
    ...panelStyle,
    flex: '0 0 30%',
    backgroundColor: 'rgba(30, 41, 59, 0.3)'
  };

  const panelHeaderStyle: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: '700',
    marginBottom: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#cbd5e1'
  };

  const categoryHeaderStyle: (color: string) => React.CSSProperties = (color) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    backgroundColor: `${color}20`,
    border: `1px solid ${color}40`,
    borderRadius: '6px',
    cursor: 'pointer',
    marginBottom: '8px',
    transition: 'all 0.2s ease',
    fontSize: '13px',
    fontWeight: '600',
    color: '#e2e8f0'
  });

  const fieldCardStyle: React.CSSProperties = {
    padding: '10px 12px',
    backgroundColor: 'rgba(15, 23, 42, 0.4)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '6px',
    marginBottom: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '13px'
  };

  const selectedFieldCardStyle: React.CSSProperties = {
    padding: '12px',
    backgroundColor: 'rgba(79, 172, 254, 0.1)',
    border: '1px solid rgba(79, 172, 254, 0.3)',
    borderRadius: '6px',
    marginBottom: '8px',
    fontSize: '13px'
  };

  const smallInputStyle: React.CSSProperties = {
    padding: '6px 8px',
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '4px',
    color: '#e2e8f0',
    fontSize: '12px',
    width: '100%',
    marginTop: '4px',
    outline: 'none'
  };

  const scrollContainerStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden'
  };

  const presetButtonStyle: (active?: boolean) => React.CSSProperties = (active = false) => ({
    padding: '8px 12px',
    backgroundColor: active ? '#4facfe' : 'rgba(79, 172, 254, 0.2)',
    border: `1px solid ${active ? '#4facfe' : 'rgba(79, 172, 254, 0.4)'}`,
    borderRadius: '4px',
    color: active ? '#fff' : '#94a3b8',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontWeight: '600'
  });

  const excelPreviewStyle: React.CSSProperties = {
    overflowX: 'auto',
    overflowY: 'auto',
    flex: 1
  };

  const excelTableStyle: React.CSSProperties = {
    borderCollapse: 'collapse',
    width: '100%',
    backgroundColor: 'rgba(15, 23, 42, 0.8)',
    fontSize: '11px'
  };

  const excelHeaderCellStyle: React.CSSProperties = {
    backgroundColor: '#10b981',
    color: '#fff',
    padding: '8px 4px',
    textAlign: 'left',
    fontWeight: '700',
    borderRight: '1px solid #0d8659'
  };

  const excelRowCellStyle: (zebra: boolean) => React.CSSProperties = (zebra) => ({
    padding: '6px 4px',
    borderRight: '1px solid rgba(255, 255, 255, 0.1)',
    backgroundColor: zebra ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
  });

  const toggleStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px'
  };

  const toggleCheckboxStyle: React.CSSProperties = {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: '#4facfe'
  };

  const savedTemplatesContainerStyle: React.CSSProperties = {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)'
  };

  const templateCardStyle: React.CSSProperties = {
    padding: '10px',
    backgroundColor: 'rgba(15, 23, 42, 0.5)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '6px',
    marginBottom: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '12px'
  };

  const messageStyle: React.CSSProperties = {
    padding: '8px 12px',
    backgroundColor: message.includes('Failed') ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)',
    border: `1px solid ${message.includes('Failed') ? 'rgba(239, 68, 68, 0.4)' : 'rgba(16, 185, 129, 0.4)'}`,
    borderRadius: '4px',
    fontSize: '12px',
    color: message.includes('Failed') ? '#fca5a5' : '#86efac'
  };

  return (
    <div style={containerStyle}>
      {/* TOP BAR */}
      <div style={topBarStyle}>
        <input
          type="text"
          placeholder="Template Name"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          style={{ ...inputStyle, flex: 0.8 }}
        />
        <select
          value={selectedCustomer}
          onChange={(e) => setSelectedCustomer(e.target.value)}
          style={selectStyle}
        >
          <option value="">Select Customer...</option>
          {customerList.map(customer => (
            <option key={customer.code} value={customer.code}>
              {customer.name}
            </option>
          ))}
        </select>
        <label style={toggleStyle}>
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            style={toggleCheckboxStyle}
          />
          <span>Default</span>
        </label>
        <button
          onClick={handleSaveTemplate}
          disabled={loading}
          style={{
            ...buttonStyle,
            opacity: loading ? 0.6 : 1,
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
          onMouseOver={(e) => {
            if (!loading) (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#3a9edb';
          }}
          onMouseOut={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#4facfe';
          }}
        >
          {loading ? 'Saving...' : 'Save Template'}
        </button>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              ...buttonStyle,
              backgroundColor: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: '#cbd5e1'
            }}
            onMouseOver={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
            }}
            onMouseOut={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
            }}
          >
            Close
          </button>
        )}
      </div>

      {/* MESSAGE */}
      {message && (
        <div style={{ padding: '0 24px', paddingTop: '8px' }}>
          <div style={messageStyle}>{message}</div>
        </div>
      )}

      {/* MAIN CONTENT */}
      <div style={mainContentStyle}>
        {/* LEFT PANEL - Available Fields */}
        <div style={leftPanelStyle}>
          <div style={panelHeaderStyle}>Available Fields</div>
          <input
            type="text"
            placeholder="Search fields..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ ...inputStyle, marginBottom: '12px', width: '100%', boxSizing: 'border-box' }}
          />
          <div style={scrollContainerStyle}>
            {Object.entries(groupedFields).map(([category, fields]) => {
              const categoryConfig = CATEGORY_CONFIG[category as keyof typeof CATEGORY_CONFIG];
              const filteredCategoryFields = fields.filter(f =>
                !searchQuery.trim() ||
                f.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                f.description.toLowerCase().includes(searchQuery.toLowerCase())
              );

              if (filteredCategoryFields.length === 0) return null;

              const isExpanded = expandedCategories.has(category);
              const selectedCount = categorySelectCount[category] || 0;

              return (
                <div key={category} style={{ marginBottom: '12px' }}>
                  <div
                    style={{
                      ...categoryHeaderStyle(categoryConfig.color),
                      cursor: 'pointer'
                    }}
                    onClick={() => toggleCategoryExpansion(category)}
                    onMouseOver={(e) => {
                      const target = e.currentTarget as HTMLDivElement;
                      target.style.backgroundColor = `${categoryConfig.color}30`;
                    }}
                    onMouseOut={(e) => {
                      const target = e.currentTarget as HTMLDivElement;
                      target.style.backgroundColor = `${categoryConfig.color}20`;
                    }}
                  >
                    <span>{categoryConfig.icon}</span>
                    <span style={{ flex: 1 }}>
                      {categoryConfig.label} ({selectedCount}/{filteredCategoryFields.length})
                    </span>
                    <span style={{ fontSize: '12px' }}>
                      {isExpanded ? '▼' : '▶'}
                    </span>
                  </div>
                  {isExpanded && (
                    <div style={{ marginTop: '8px', marginLeft: '4px' }}>
                      {filteredCategoryFields.map(field => {
                        const isSelected = selectedFieldKeys.has(field.key);
                        return (
                          <div
                            key={field.key}
                            style={{
                              ...fieldCardStyle,
                              backgroundColor: isSelected ? 'rgba(15, 23, 42, 0.8)' : 'rgba(15, 23, 42, 0.4)',
                              opacity: isSelected ? 0.5 : 1,
                              pointerEvents: isSelected ? 'none' : 'auto',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}
                            onMouseOver={(e) => {
                              if (!isSelected) (e.currentTarget as HTMLDivElement).style.backgroundColor = 'rgba(79, 172, 254, 0.15)';
                            }}
                            onMouseOut={(e) => {
                              if (!isSelected) (e.currentTarget as HTMLDivElement).style.backgroundColor = 'rgba(15, 23, 42, 0.4)';
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '600', marginBottom: '2px' }}>{field.label}</div>
                              <div style={{ fontSize: '11px', color: '#94a3b8' }}>{field.description}</div>
                            </div>
                            {isSelected ? (
                              <div style={{ marginLeft: '8px', color: '#10b981' }}>✓</div>
                            ) : (
                              <button
                                onClick={() => addField(field.key)}
                                style={{
                                  marginLeft: '8px',
                                  padding: '4px 8px',
                                  backgroundColor: 'rgba(79, 172, 254, 0.3)',
                                  border: '1px solid rgba(79, 172, 254, 0.5)',
                                  borderRadius: '4px',
                                  color: '#7dd3fc',
                                  fontSize: '11px',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseOver={(e) => {
                                  const target = e.currentTarget as HTMLButtonElement;
                                  target.style.backgroundColor = 'rgba(79, 172, 254, 0.5)';
                                }}
                                onMouseOut={(e) => {
                                  const target = e.currentTarget as HTMLButtonElement;
                                  target.style.backgroundColor = 'rgba(79, 172, 254, 0.3)';
                                }}
                              >
                                Add →
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* CENTER PANEL - Selected Fields */}
        <div style={centerPanelStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={panelHeaderStyle}>Selected Columns ({selectedFields.length})</div>
            {selectedFields.length > 0 && (
              <button
                onClick={() => setSelectedFields([])}
                style={{
                  padding: '6px 10px',
                  backgroundColor: 'rgba(239, 68, 68, 0.2)',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  borderRadius: '4px',
                  color: '#fca5a5',
                  fontSize: '11px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(239, 68, 68, 0.3)';
                }}
                onMouseOut={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(239, 68, 68, 0.2)';
                }}
              >
                Clear All
              </button>
            )}
          </div>

          {/* QUICK PRESETS */}
          <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '8px', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.5px' }}>
              Quick Presets
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {QUICK_PRESETS.map(preset => (
                <button
                  key={preset.name}
                  onClick={() => applyPreset(preset.fields)}
                  style={presetButtonStyle()}
                  onMouseOver={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(79, 172, 254, 0.3)';
                  }}
                  onMouseOut={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(79, 172, 254, 0.2)';
                  }}
                >
                  {preset.name}
                </button>
              ))}
            </div>
          </div>

          {selectedFields.length === 0 ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#64748b',
              textAlign: 'center',
              fontSize: '12px'
            }}>
              Drag fields from the left panel or click Add → to build your template
            </div>
          ) : (
            <div style={scrollContainerStyle}>
              {selectedFields.map((field, index) => {
                const fieldDef = AVAILABLE_FIELDS.find(f => f.key === field.key);
                return (
                  <div key={`${field.key}-${index}`} style={selectedFieldCardStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ color: '#94a3b8', cursor: 'grab' }}>⋮⋮</span>
                      <div style={{ flex: 1, fontWeight: '600' }}>
                        {fieldDef?.label || field.label}
                      </div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          onClick={() => moveField(index, 'up')}
                          disabled={index === 0}
                          style={{
                            padding: '4px 6px',
                            backgroundColor: index === 0 ? 'rgba(255, 255, 255, 0.05)' : 'rgba(79, 172, 254, 0.2)',
                            border: '1px solid rgba(79, 172, 254, 0.3)',
                            borderRadius: '3px',
                            color: '#7dd3fc',
                            cursor: index === 0 ? 'not-allowed' : 'pointer',
                            fontSize: '10px',
                            opacity: index === 0 ? 0.5 : 1
                          }}
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => moveField(index, 'down')}
                          disabled={index === selectedFields.length - 1}
                          style={{
                            padding: '4px 6px',
                            backgroundColor: index === selectedFields.length - 1 ? 'rgba(255, 255, 255, 0.05)' : 'rgba(79, 172, 254, 0.2)',
                            border: '1px solid rgba(79, 172, 254, 0.3)',
                            borderRadius: '3px',
                            color: '#7dd3fc',
                            cursor: index === selectedFields.length - 1 ? 'not-allowed' : 'pointer',
                            fontSize: '10px',
                            opacity: index === selectedFields.length - 1 ? 0.5 : 1
                          }}
                        >
                          ↓
                        </button>
                        <button
                          onClick={() => removeField(field.key)}
                          style={{
                            padding: '4px 6px',
                            backgroundColor: 'rgba(239, 68, 68, 0.2)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '3px',
                            color: '#fca5a5',
                            cursor: 'pointer',
                            fontSize: '10px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseOver={(e) => {
                            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(239, 68, 68, 0.3)';
                          }}
                          onMouseOut={(e) => {
                            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(239, 68, 68, 0.2)';
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <label style={{ fontSize: '11px', color: '#cbd5e1' }}>Custom Header</label>
                      <input
                        type="text"
                        value={field.customHeader || field.label}
                        onChange={(e) => updateFieldHeader(index, e.target.value)}
                        style={smallInputStyle}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: '#cbd5e1' }}>Column Width</label>
                      <select
                        value={field.columnWidth || 'medium'}
                        onChange={(e) => updateFieldWidth(index, e.target.value)}
                        style={smallInputStyle}
                      >
                        <option value="narrow">Narrow (10)</option>
                        <option value="medium">Medium (15)</option>
                        <option value="wide">Wide (25)</option>
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* RIGHT PANEL - Excel Preview */}
        <div style={rightPanelStyle}>
          <div style={panelHeaderStyle}>Excel Preview</div>
          <div style={excelPreviewStyle}>
            {selectedFields.length === 0 ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: '#64748b',
                textAlign: 'center',
                fontSize: '12px'
              }}>
                Select fields to see preview
              </div>
            ) : (
              <table style={excelTableStyle}>
                <thead>
                  <tr>
                    <th style={{ ...excelHeaderCellStyle, width: '30px', padding: '8px 4px' }}>
                      <span style={{ color: '#cbd5e1' }}>A</span>
                    </th>
                    {selectedFields.map((field, idx) => (
                      <th key={`header-${idx}`} style={{
                        ...excelHeaderCellStyle,
                        width: `${getColumnWidth(field.columnWidth)}ch`,
                        minWidth: `${getColumnWidth(field.columnWidth) * 8}px`
                      }}>
                        {field.customHeader || field.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[0, 1, 2].map((rowIdx) => (
                    <tr key={`row-${rowIdx}`}>
                      <td style={{
                        ...excelRowCellStyle(false),
                        width: '30px',
                        padding: '6px 4px',
                        textAlign: 'center',
                        fontWeight: '600',
                        color: '#cbd5e1'
                      }}>
                        {rowIdx + 2}
                      </td>
                      {selectedFields.map((field, colIdx) => {
                        const sampleValue = SAMPLE_DATA[field.key]?.[rowIdx] || '-';
                        return (
                          <td key={`cell-${rowIdx}-${colIdx}`} style={{
                            ...excelRowCellStyle(rowIdx % 2 === 1),
                            width: `${getColumnWidth(field.columnWidth)}ch`,
                            minWidth: `${getColumnWidth(field.columnWidth) * 8}px`
                          }}>
                            {sampleValue}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* BOTTOM BAR - Saved Templates */}
      {savedTemplates.length > 0 && (
        <div style={{
          padding: '16px 24px',
          backgroundColor: 'rgba(30, 41, 59, 0.4)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          maxHeight: '120px',
          overflowY: 'auto'
        }}>
          <div style={{ fontSize: '12px', fontWeight: '700', marginBottom: '10px', color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Saved Templates ({savedTemplates.length})
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {savedTemplates.map(template => (
              <div
                key={template.id}
                style={{
                  ...templateCardStyle,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  minWidth: '200px'
                }}
                onMouseOver={(e) => {
                  (e.currentTarget as HTMLDivElement).style.backgroundColor = 'rgba(79, 172, 254, 0.15)';
                }}
                onMouseOut={(e) => {
                  (e.currentTarget as HTMLDivElement).style.backgroundColor = 'rgba(15, 23, 42, 0.5)';
                }}
              >
                <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => loadTemplate(template)}>
                  <div style={{ fontWeight: '600', marginBottom: '2px' }}>{template.name}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                    {template.customerCode && `${template.customerCode} • `}
                    {template.fields.length} columns
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteTemplate(template.id)}
                  style={{
                    padding: '4px 6px',
                    backgroundColor: 'rgba(239, 68, 68, 0.2)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '3px',
                    color: '#fca5a5',
                    cursor: 'pointer',
                    fontSize: '10px',
                    transition: 'all 0.2s ease',
                    marginLeft: '8px'
                  }}
                  onMouseOver={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(239, 68, 68, 0.3)';
                  }}
                  onMouseOut={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(239, 68, 68, 0.2)';
                  }}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExcelTemplateBuilder;
